import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home, 
  Boxes, 
  Plus,
  Menu,
  Search,
  X,
  ChevronDown,
  Settings,
  ChevronLeft,
  ChevronRight,
  DoorOpen,
  Power
} from 'lucide-react';

import Profile from './Profile';
import {searchByName} from "../api/api";
import config from '../utils/config';
import { useAuth } from '../utils/AuthContext';


const HomeStickyNavbar = () => {
  
  const { token, logout } = useAuth();

  const searchContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Check if route is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Animation variants for the toggle button
  const toggleVariants = {
    open: { scale: 1 },
    closed: { scale: 1 },
    hover: { scale: 1.05 }
  };

  // Handle scroll effect
  useEffect(() => {

    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchExpanded(false);  // Shrink search bar
        setShowResults(false);     // Also hide results
      }
    };

    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle clicking outside to close results
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.getElementById('search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}>

      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Left Section: Sidebar Toggle + Logo */}
          <div className="flex items-center space-x-3">
            <motion.button 
              className={`p-2 rounded-lg transition-colors duration-300 hover:bg-red-100`}
              onClick={() => {
                  logout();
                  navigate('/landing');
                }}
              aria-label='Back'
              variants={toggleVariants}
              initial='open'
              animate='open'
              whileHover="hover"
              title='Back '>
                <Power color="#b51a00" strokeWidth={1.5} />
              
            </motion.button>

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <img 
                src="/tuly.png"
                alt="Tuly Logo" 
                className="h-12 w-auto transition-transform group-hover:scale-105"
              />
            </Link>
          </div>

          {/* Page Title - Center */}
          <h1 className="text-lg font-semibold text-gray-800 absolute left-1/2 transform -translate-x-1/2">
            Service Gates
          </h1>


          {/* Right Section: Search and Profile */}
          <div className="flex items-center space-x-2">

            {/* Profile Component with slight margin */}
            <div className="ml-2">
              <Profile />
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden flex items-center justify-center h-8 w-8 rounded-md text-gray-600 hover:bg-gray-100"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

    </nav>
  );
};

export default HomeStickyNavbar;